'use client';

import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Global promise to track script loading state
 * This ensures the script is only loaded once across all components
 */
let googleMapsLoadPromise: Promise<void> | null = null;

/**
 * Custom hook to load Google Maps JavaScript API script
 * Ensures the script is only loaded once globally, preventing duplicate script errors
 *
 * @param enabled - Set false to hold the script back until it is actually
 *   needed. Address autocomplete on the profile form only wants Places once
 *   the user reaches the address field, and most visitors to a page never do,
 *   so loading ~100KB of Maps on mount would be waste. Defaults to true, which
 *   is the behaviour every map component here relies on.
 * @returns {boolean} isLoaded - Whether the Google Maps script is loaded and ready
 */
export function useGoogleMaps(enabled: boolean = true): boolean {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.error('Google Maps API key is not configured');
      toast.error('Google Maps API key is not configured. Please contact support.');
      return;
    }

    // Check if already loaded
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // If script is already being loaded, wait for it
    if (googleMapsLoadPromise) {
      googleMapsLoadPromise
        .then(() => {
          setIsLoaded(true);
        })
        .catch(() => {
          // Error already handled in the loading promise
        });
      return;
    }

    // Check if script tag already exists in DOM
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );

    if (existingScript) {
      // Script tag exists, wait for it to load
      // Check if it's already loaded first
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        return;
      }

      // Create promise to wait for existing script to load
      if (!googleMapsLoadPromise) {
        googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
          const checkInterval = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);

          // Timeout after 10 seconds
          const timeoutId = setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error('Timeout waiting for Google Maps to load'));
          }, 10000);

          existingScript.addEventListener('load', () => {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            resolve();
          });

          existingScript.addEventListener('error', () => {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            reject(new Error('Failed to load Google Maps script'));
          });
        });
      }

      googleMapsLoadPromise
        .then(() => {
          setIsLoaded(true);
        })
        .catch((error) => {
          console.error('Error loading Google Maps:', error);
          toast.error('Failed to load Google Maps. Please check your internet connection.');
          googleMapsLoadPromise = null; // Reset on error
        });
      return;
    }

    // Load the script
    googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        setIsLoaded(true);
        resolve();
      };

      script.onerror = () => {
        console.error('Failed to load Google Maps script');
        toast.error('Failed to load Google Maps. Please check your internet connection.');
        googleMapsLoadPromise = null; // Reset so we can retry
        reject(new Error('Failed to load Google Maps script'));
      };

      document.head.appendChild(script);
    });
  }, [enabled]);

  return isLoaded;
}

